/* WOZ motion layer. Desktop: Lenis smooth scrolling. All devices: scroll reveals.
   ICF page: reading progress + hero parallax. Skipped entirely for reduced motion and in the Theme Editor. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || window.Shopify && window.Shopify.designMode) return;
  const root = document.documentElement;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Smooth scroll (desktop only) ---------- */
  let lenis = null;
  if (finePointer && window.Lenis) {
    lenis = new window.Lenis({
      lerp: 0.09,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false,
      allowNestedScroll: true,
      anchors: false,
      prevent: (node) => !!(node.closest && node.closest(
        'dialog, .sidebar, .site-sidebar, cart-drawer, .cart-drawer, .quick-view, .modal, [role="dialog"], .search-results, .rps-container, [class*="rapid"], .predictive-search, [data-lenis-prevent]'))
    });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    window.wozLenis = lenis;

    // Pause when the theme locks scrolling (drawers, menus, quick view, lightbox)
    const locked = () => {
      const b = document.body;
      const cs = getComputedStyle(b), hs = getComputedStyle(root);
      return cs.overflow === 'hidden' || cs.overflowY === 'hidden' || hs.overflowY === 'hidden' ||
        cs.position === 'fixed' || !!document.querySelector('dialog[open]');
    };
    const sync = () => { if (locked()) { if (!lenis.isStopped) lenis.stop(); } else if (lenis.isStopped) lenis.start(); };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['style'] });
    document.addEventListener('toggle', sync, true);
    document.addEventListener('close', sync, true);

    // In-page anchors: glide, respecting each section's scroll-margin
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a || e.defaultPrevented) return;
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const el = document.getElementById(decodeURIComponent(id.slice(1)));
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { duration: 1.2 }); // Lenis already honours scroll-margin-top
      history.replaceState(null, '', id);
    });
  }

  /* ---------- Scroll reveals ---------- */
  const isIcf = !!document.querySelector('.wi-campaign');
  const selectors = isIcf ? [
    '.wi-campaign .section-head', '.wi-campaign .product-card', '.wi-campaign .print-path',
    '.wi-campaign .impact-inner > *', '.wi-campaign .mission-heading', '.wi-campaign .mission-intro',
    '.wi-campaign .mission-pillars > *', '.wi-campaign .gallery-grid > *', '.wi-campaign .questions > *',
    '.wi-campaign .closing .wrap > *', '.wi-campaign .edition', '.wi-campaign .pledges > *'
  ] : [
    '.wozv4-featured-head > *', '.wozv4-stmt-head > *', '.wozv4-tm-head > *', '.wozv4-cats-head > *',
    '.wozv4-card', '.wozv4-stmt-card', '.wozv4-tm-card', '.wozv4-cat-card',
    '.wozv4-story-portrait', '.wozv4-story-copy > *', '.wozv4-press-list > *',
    '.woz-visit__head > *', '.woz-visit__grid > *', '.wozv4-closing .wozv4-container > *',
    'main .shopify-section:not(:first-child) h2',
    'main .shopify-section:not(:first-child) .product-item',
    'main .shopify-section:not(:first-child) .collection-item',
    'main .shopify-section:not(:first-child) .card:not(.product-item .card):not(.collection-item .card)',
    'main .shopify-section:not(:first-child) .text-columns__item',
    'main .shopify-section:not(:first-child) .rte > p',
    'main .shopify-section:not(:first-child) .button:not(.product-item .button)'
  ];
  const media = isIcf ? '.wi-campaign .product-image, .wi-campaign .gallery-grid figure' : '.wozv4-card-imglink, .wozv4-stmt-img, .wozv4-tm-img, .wozv4-cat-img';

  const vh = window.innerHeight;
  const targets = [];
  const seen = new Set();
  document.querySelectorAll(selectors.join(',')).forEach((el) => {
    if (seen.has(el) || el.closest('header, footer, .category-nav, dialog, .sidebar, cart-drawer, .quick-view')) return;
    // skip nested duplicates (parent already animates)
    for (const p of seen) if (p.contains(el)) return;
    const r = el.getBoundingClientRect();
    if (r.top < vh * 0.92 || r.height === 0) return; // never hide what's already on screen
    seen.add(el); targets.push(el);
  });
  if (media) document.querySelectorAll(media).forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top > vh * 0.92) el.classList.add('wm-media');
  });

  // Stagger siblings in grids
  targets.forEach((el) => {
    el.classList.add('wm-reveal');
    const parent = el.parentElement;
    const sibs = [...parent.children].filter((c) => targets.includes(c));
    const i = sibs.indexOf(el);
    if (i > 0) el.style.setProperty('--wm-delay', Math.min(i % 4, 3) * 0.09 + 's');
  });
  root.classList.add('wm-on');

  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('wm-in'); io.unobserve(en.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  document.querySelectorAll('.wm-reveal, .wm-media').forEach((el) => io.observe(el));
  // Fail-safe: reveal on plain scroll checks too, in case the observer is throttled
  let t = null;
  const sweep = () => { t = null; const h = window.innerHeight;
    document.querySelectorAll('.wm-reveal:not(.wm-in), .wm-media:not(.wm-in)').forEach((el) => {
      if (el.getBoundingClientRect().top < h * 0.95) el.classList.add('wm-in'); }); };
  window.addEventListener('scroll', () => { if (!t) t = setTimeout(sweep, 120); }, { passive: true });
  setTimeout(sweep, 1500);
  // Safety: anything still hidden after a jump (search, anchor, back button) shows on the next frame it's near view
  window.addEventListener('pageshow', () => document.querySelectorAll('.wm-reveal:not(.wm-in)').forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('wm-in');
  }));

  /* ---------- ICF: progress line + hero parallax ---------- */
  if (isIcf) {
    const bar = document.createElement('div');
    bar.className = 'wm-progress'; bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    const hero = document.querySelector('.wi-campaign .hero-art');
    let ticking = false;
    const update = () => {
      ticking = false;
      const max = root.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(1, window.scrollY / max) : 0) + ')';
      if (hero) {
        const r = hero.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight) hero.style.setProperty('--wm-p', (Math.max(-14, Math.min(14, -r.top * 0.05))).toFixed(1));
      }
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    if (lenis) lenis.on('scroll', onScroll); else window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }
})();
